<!--
  @description Controlled text input with inline validation, optional label, and live border
  feedback. Use for any single-line text, number, email, or password field in the admin and
  auth forms. Validation runs on every keystroke after the first user interaction; border turns
  red on failure and green on success.

  The `validation` prop accepts optional required, length-range, and regex rules. The
  component drives its own internal `Status` and emits it alongside the raw value on every
  change so parent forms can gate submission without re-running validation logic themselves.

  @example
  <Input
    id="pack-name"
    title="Pack name"
    :value="form.name"
    :validation="{ required: true, length: { min: 3, max: 80 } }"
    @update:modelValue="(val, status) => { form.name = val; nameStatus = status }"
  />

  @accessibility The `<label>` is tied to the `<input>` via matching `id` / `for` attributes.
  Error messages are rendered in a `<p>` immediately after the input — screen readers will
  announce them if the element is programmatically focused. Ensure `id` values are unique
  per page to avoid duplicate-id accessibility violations.
-->
<template>
  <div class="input">
    <label v-if="title !== undefined" :for="id" class="input__label primary"
      >{{ title }}<span v-if="!validation.required"> ({{ $t('global.optional') }})</span></label
    >
    <input
      :id="id"
      :name="title"
      :type="type"
      :step="step"
      :placeholder="placeholder"
      :class="classes"
      :value="inputValue"
      :min="minMax?.min || validation.length?.min"
      :max="minMax?.max || validation.length?.max"
      :aria-invalid="!status.valid && status.touched ? true : undefined"
      :aria-describedby="!status.valid ? `${id}-error` : undefined"
      class="input__field"
      @input="updateValue"
    />
    <p v-if="!status.valid" :id="`${id}-error`" role="alert" class="input__error">
      {{ status.message }}
    </p>
  </div>
</template>

<script setup lang="ts">
import type { Status, ValidationParams } from '@/types/Types'
import { validate, required, length, regex } from '@/Validation/validation'

import { computed, ref, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  /** Unique HTML id — wired to the `<input>` element and matched by the label's `for`. */
  id: {
    type: String,
    required: true
  },
  /** Label text rendered above the input. Omit to render an unlabelled input. */
  title: {
    type: String,
    required: false,
    default: undefined
  },
  /** Controlled current value. Synced via `watchEffect` so external changes propagate in. */
  value: {
    type: String,
    required: false,
    default: ''
  },
  /** HTML input type — e.g. `'text'`, `'number'`, `'email'`, `'password'`. Defaults to `'text'`. */
  type: {
    type: String,
    required: false,
    default: 'text'
  },
  /** Forwarded to the native `step` attribute; useful for `type="number"` inputs. */
  step: {
    type: String,
    required: false,
    default: undefined
  },
  placeholder: {
    type: String,
    required: false,
    default: undefined
  },
  /**
   * Validation rules applied on every keystroke after the first user interaction.
   * - `required` — rejects empty values
   * - `length` — enforces min/max character count
   * - `regex` — tests the value against a custom pattern with a caller-supplied error message
   */
  validation: {
    type: Object as () => ValidationParams,
    required: false,
    default: () => ({
      required: false,
      length: null,
      regex: null
    })
  },
  /**
   * Numeric min/max forwarded directly to the native `<input>` attributes.
   * Takes precedence over `validation.length` for the rendered HTML attribute when both
   * are provided. Use for `type="number"` range constraints.
   */
  minMax: {
    type: Object as () => { min: number; max: number },
    required: false
  }
})

const emit = defineEmits<{
  (event: 'update:modelValue', value: string, status: Status): void
}>()

const updateValue = (e: Event) => {
  const target = (e.target as HTMLInputElement).value
  validateInput(target)
  emit('update:modelValue', target, status.value)
}

const classes = computed(() => {
  return {
    'border-danger': !status.value.valid && status.value.touched,
    'border-success': status.value.valid && status.value.touched
  }
})

const status = ref({ valid: true, message: '', touched: false } as Status)

function validateInput(value: string) {
  const rules = []
  if (props.validation.required) rules.push(required)
  if (props.validation.length) rules.push(length(props.validation.length, t))
  if (props.validation.regex)
    rules.push(
      regex({ regex: props.validation.regex.value, message: props.validation.regex.message }, t)
    )
  status.value = validate(value, rules, t)
}

const inputValue = ref(props.value)

watchEffect(() => {
  inputValue.value = props.value
})

const isFirstRender = ref(true)

watchEffect(() => {
  if (isFirstRender.value) {
    isFirstRender.value = false
    return
  }
  validateInput(inputValue.value)
})
</script>

<style lang="scss" scoped>
.input {
  display: flex;
  flex-direction: column;
  margin: 1rem 0.5rem;

  &--tiny {
    margin: 0;

    .input__field {
      border-radius: $small-border-radius !important;
      font-size: $font-size-sm;
      width: 100%;
      max-width: 10px;
      padding: 0.1rem 0.5rem;

      @include min-breakpoint-md {
        max-width: 30px;
      }
    }
  }

  &__label {
    margin-bottom: 0.5rem;
    font-weight: bold;
    color: var(--text);
  }

  &__field {
    padding: $half-spacing $base-spacing;
    border: 1px solid var(--input-background);
    border-radius: $xl-border-radius;
    font-size: 1rem;
    background-color: var(--input-background);
    color: var(--text);
    width: calc(100% - 2rem);

    &:focus {
      outline: none;
      border: 1px solid var(--primary);
    }
  }

  &__error {
    margin-top: 0.2rem;
    font-size: 0.8rem;
    color: var(--danger);
  }
}
</style>
